import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { fetchOffers, type Offer } from '../../../lib/offers';
import type { Client } from '../../../context/ClientContext';
import { Button, Card, Dropdown, Input, Switch, Table, TableColumn, useToast } from '../../ui';
import AhrefsProjectPicker from './pages/AhrefsProjectPicker';
import PageDetailView from './pages/PageDetailView';
import SortHeader, { type SortState } from './functions/SortHeader';
import { sortRows } from './functions/sortRows';
import { fmtDay, fmtDec, fmtInt, fmtPct, fmtWhen, hostOf, isUnderLinked, LINK_FLOOR, pageFlags, pathOf, type PageRow } from './pages/format';

/**
 * A page's journal at a glance: how many entries, whether an agent job is open on it, when one last shipped, when
 * someone last logged a Search Console indexing request for it, and the newest entry.
 */
type JournalState = {
  entries: number;
  working: boolean;
  shippedAt: string | null;
  indexRequestedAt: string | null;
  last: { kind: string; at: string } | null;
};
/** Journal entry kinds, short enough for a table cell. */
const JOURNAL_SHORT: Record<string, string> = {
  retro_opt: 'Retro opt',
  density_fix: 'Density fix',
  template_fix: 'Template fix',
  links_fix: 'Links fix',
  index_request: 'Index request',
};
/** Google's coverage states (URL Inspection), short, with a tone: indexed, not indexed, never seen. */
const INDEX_SHORT: Record<string, [string, string]> = {
  'Submitted and indexed': ['Indexed', 'var(--status-up)'],
  'Indexed, not submitted in sitemap': ['Indexed', 'var(--status-up)'],
  'Crawled - currently not indexed': ['Crawled, not indexed', 'var(--status-down)'],
  'Discovered - currently not indexed': ['Discovered', 'var(--status-warn)'],
  'URL is unknown to Google': ['Unknown to Google', 'var(--text-faint)'],
  'Duplicate, Google chose different canonical than user': ['Duplicate', 'var(--status-down)'],
  'Excluded by ‘noindex’ tag': ['Noindex', 'var(--text-faint)'],
};
const HIDE_B_KEY = 'cockpit.pages.hideTierB';
/** An indexing request this recent means the page is waiting on Google, not forgotten. */
const INDEX_REQUEST_FRESH_MS = 30 * 86400000;
/** Job statuses that are no longer work in progress. */
const CLOSED_JOB = ['complete', 'submitted', 'cancelled'];
/** A timestamp as "Oct 16" in local time. */
const fmtShortDate = (iso: string) => new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

interface PagesSectionProps {
  client: Client;
  expandedSection: string | null;
  toggleSection: (section: string) => void;
}

type SortField = 'tier' | 'url' | 'gsc_clicks' | 'gsc_impressions' | 'gsc_ctr' | 'gsc_position' | 'gsc_queries' | 'index_coverage' | 'inlinks';

const FETCH_PAGE = 1000;

const faint: React.CSSProperties = { color: 'var(--text-faint)' };
const oneLine: React.CSSProperties = { display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
// The table sits inside the section card, so it drops its own card chrome.
const flatTable: React.CSSProperties = { background: 'transparent', border: 'none', borderRadius: 0, boxShadow: 'none', minWidth: '920px' };

/** The number, then how it moved against the window before. `lowerIsBetter` is for position. */
function Moved({ now, before, lowerIsBetter = false, digits = 0 }: { now: number | null; before: number | null; lowerIsBetter?: boolean; digits?: number }) {
  const diff = now != null && before != null ? Number(now) - Number(before) : 0;
  const shown = Math.abs(diff) >= (digits ? 0.05 : 1);
  const good = lowerIsBetter ? diff < 0 : diff > 0;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: '5px' }}>
      {shown ? (
        <span style={{ fontSize: 'var(--text-2xs)', color: good ? 'var(--status-up)' : 'var(--status-down)' }}>
          {good ? '▲' : '▼'}
          {digits ? Math.abs(diff).toFixed(digits) : fmtInt(Math.abs(diff))}
        </span>
      ) : null}
      {now == null ? <span style={faint}>—</span> : digits ? fmtDec(now, digits) : fmtInt(now)}
    </span>
  );
}

/**
 * Client detail → Pages: every live URL on the client's site with how it does in search
 * (Search Console, via the client's Ahrefs project). Selecting a row swaps the section
 * for that URL's detail. The pages sync writes the rows (scripts/lib/pages-sync.mjs); the only thing
 * set here is which Ahrefs project is the client's site.
 */
export default function PagesSection({ client, expandedSection }: PagesSectionProps) {
  const [rows, setRows] = useState<PageRow[]>([]);
  const [offers, setOffers] = useState<Offer[]>([]);
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  // Hide tier B (articles, case studies, hubs) to see just the money pages. Remembered in this browser.
  const [hideB, setHideB] = useState(() => localStorage.getItem(HIDE_B_KEY) === '1');
  const toggleHideB = (hide: boolean) => {
    setHideB(hide);
    localStorage.setItem(HIDE_B_KEY, hide ? '1' : '0');
  };
  // The open page and the filter live in the URL (?page=<id>, ?show=<filter>) so a refresh stays put.
  const [searchParams, setSearchParams] = useSearchParams();
  const show = searchParams.get('show') ?? 'all';
  const selectedId = searchParams.get('page');
  const setParam = (key: string, value: string | null) =>
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value == null) next.delete(key);
      else next.set(key, value);
      return next;
    });
  const setShow = (value: string) => setParam('show', value === 'all' ? null : value);
  const setSelectedId = (id: string | null) => setParam('page', id);
  const [sort, setSort] = useState<SortState<SortField>>({ field: 'gsc_clicks', dir: 'desc' });
  // Set when the last sync attempt for this client failed (the schedule records every attempt on the client).
  const [syncError, setSyncError] = useState<{ at: string | null; message: string } | null>(null);
  // clients.ahrefs_project_id; undefined until the client row has loaded.
  const [projectId, setProjectId] = useState<number | null | undefined>(undefined);
  // clients.pages_sync_limit: big sites sync only their top N pages by Ahrefs traffic.
  const [syncLimit, setSyncLimit] = useState<number | null>(null);
  const sectionRef = useRef<HTMLDivElement>(null);
  // Where the list was scrolled to when a row was opened, so Back lands on the same row.
  const listScroll = useRef<number | null>(null);

  // The page journal (page_optimizations) with each entry's job status: amber while an agent job is open on a page,
  // green once one shipped after the page's last sync (the next sync re-measures it, and the green goes).
  const [journal, setJournal] = useState<Map<string, JournalState>>(new Map());
  const fetchJournal = useCallback(async () => {
    if (!client?.id) return;
    const { data, error: jErr } = await supabase
      .from('page_optimizations')
      .select('page_id, kind, created_at, shipped_at, job:ai_jobs(status, updated_at)')
      .eq('client_id', client.id);
    if (jErr) {
      console.error('Error loading the page journal:', jErr);
      return;
    }
    const map = new Map<string, JournalState>();
    for (const e of (data ?? []) as unknown as {
      page_id: string;
      kind: string;
      created_at: string;
      shipped_at: string | null;
      job: { status: string; updated_at: string } | null;
    }[]) {
      const cur = map.get(e.page_id) ?? { entries: 0, working: false, shippedAt: null, indexRequestedAt: null, last: null };
      cur.entries += 1;
      if (!cur.last || e.created_at > cur.last.at) cur.last = { kind: e.kind, at: e.created_at };
      if (e.kind === 'index_request' && (!cur.indexRequestedAt || e.created_at > cur.indexRequestedAt)) cur.indexRequestedAt = e.created_at;
      const status = e.job?.status ?? null;
      if (status && !CLOSED_JOB.includes(status)) cur.working = true;
      if (status === 'complete' || status === 'submitted') {
        const at = e.shipped_at ?? e.job?.updated_at ?? null;
        if (at && (!cur.shippedAt || at > cur.shippedAt)) cur.shippedAt = at;
      }
      map.set(e.page_id, cur);
    }
    setJournal(map);
  }, [client?.id]);

  const fetchRows = useCallback(async () => {
    if (!client?.id) return;
    try {
      setIsLoading(true);
      setError(null);
      // PostgREST caps a response at 1000 rows; a big site has more pages than that.
      const all: PageRow[] = [];
      for (let from = 0; ; from += FETCH_PAGE) {
        const { data, error: fetchError } = await supabase
          .from('client_pages')
          .select('*')
          .eq('client_id', client.id)
          .order('gsc_impressions', { ascending: false, nullsFirst: false })
          .order('url')
          .range(from, from + FETCH_PAGE - 1);
        if (fetchError) throw fetchError;
        all.push(...(data ?? []));
        if (!data || data.length < FETCH_PAGE) break;
      }
      setRows(all);
      await fetchJournal();
      setOffers((await fetchOffers(client.id)).filter((o) => o.status !== 'denied'));
      const { data: state } = await supabase.from('clients').select('ahrefs_project_id, pages_sync_at, pages_sync_error, pages_sync_limit').eq('id', client.id).maybeSingle();
      setProjectId(state?.ahrefs_project_id ?? null);
      setSyncLimit(state?.pages_sync_limit ?? null);
      setSyncError(state?.pages_sync_error ? { at: state.pages_sync_at, message: state.pages_sync_error } : null);
    } catch (err) {
      console.error('Error fetching client pages:', err);
      setError('Failed to load pages');
    } finally {
      setIsLoading(false);
    }
  }, [client?.id, fetchJournal]);

  useEffect(() => {
    if (expandedSection === 'pages') fetchRows();
  }, [expandedSection, fetchRows]);

  // Agent jobs move on their own: keep the amber and green current while the Pages screen is open.
  useEffect(() => {
    if (expandedSection !== 'pages') return;
    const t = window.setInterval(() => void fetchJournal(), 60_000);
    return () => window.clearInterval(t);
  }, [expandedSection, fetchJournal]);

  const jobState = useCallback(
    (r: PageRow): 'working' | 'shipped' | null => {
      const j = journal.get(r.id);
      if (!j) return null;
      if (j.working) return 'working';
      if (j.shippedAt && (!r.synced_at || j.shippedAt > r.synced_at)) return 'shipped';
      return null;
    },
    [journal],
  );

  // The detail is shorter than the list: bring its top into view on open, and put the list back on return.
  useLayoutEffect(() => {
    if (selectedId) {
      const top = sectionRef.current?.getBoundingClientRect().top ?? 0;
      if (top < 0) window.scrollBy({ top: top - 16 });
    } else if (listScroll.current != null) {
      window.scrollTo({ top: listScroll.current });
      listScroll.current = null;
    }
  }, [selectedId]);

  // The host most rows share; anything else (a subdomain GSC reported) shows its host too.
  const mainHost = useMemo(() => {
    const counts = new Map<string, number>();
    rows.forEach((r) => counts.set(hostOf(r.url), (counts.get(hostOf(r.url)) ?? 0) + 1));
    return [...counts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? '';
  }, [rows]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const list = rows.filter((r) => {
      if (show === 'clicks' && !r.gsc_clicks) return false;
      if (show === 'unclicked' && !(r.gsc_impressions && !r.gsc_clicks)) return false;
      if (show === 'unseen' && (r.gsc_impressions || !r.in_crawl)) return false;
      if (show === 'flagged' && pageFlags(r).length === 0) return false;
      if (show === 'over' && r.overopt_level !== 'over') return false;
      if (show === 'under' && r.overopt_level !== 'under') return false;
      if (show === 'templated' && r.template_level !== 'templated') return false;
      if (show === 'few-links' && !isUnderLinked(r)) return false;
      if (show === 'money' && r.tier !== 'A') return false;
      if (show === 'no-offer' && !(r.tier === 'A' && !r.offer_id)) return false;
      if (show.startsWith('offer:') && r.offer_id !== show.slice(6)) return false;
      if (show === 'other' && r.tier !== 'B') return false;
      // Picking "Other pages (B)" in the filter means show them, toggle or not.
      if (hideB && show !== 'other' && r.tier === 'B') return false;
      if (show === 'journal' && !journal.get(r.id)?.entries) return false;
      if (show === 'index-requested' && !journal.get(r.id)?.indexRequestedAt) return false;
      if (show === 'unseen-unrequested' && (r.gsc_impressions || !r.in_crawl || journal.get(r.id)?.indexRequestedAt)) return false;
      if (show === 'working' && jobState(r) !== 'working') return false;
      if (show === 'shipped' && jobState(r) !== 'shipped') return false;
      if (!term) return true;
      return r.url.toLowerCase().includes(term) || (r.title ?? '').toLowerCase().includes(term) || (r.gsc_top_query ?? '').toLowerCase().includes(term);
    });
    return sortRows(list, sort);
  }, [rows, search, show, sort, journal, jobState, hideB]);

  const totals = useMemo(() => {
    const sum = (pick: (r: PageRow) => number | null) => rows.reduce((n, r) => n + (pick(r) ?? 0), 0);
    return {
      clicks: sum((r) => r.gsc_clicks),
      clicksPrev: sum((r) => r.gsc_clicks_prev),
      impressions: sum((r) => r.gsc_impressions),
      impressionsPrev: sum((r) => r.gsc_impressions_prev),
      live: rows.filter((r) => r.in_crawl).length,
      unseen: rows.filter((r) => r.in_crawl && !r.gsc_impressions).length,
      over: rows.filter((r) => r.overopt_level === 'over').length,
      under: rows.filter((r) => r.overopt_level === 'under').length,
      templated: rows.filter((r) => r.template_level === 'templated').length,
      fewLinks: rows.filter(isUnderLinked).length,
      money: rows.filter((r) => r.tier === 'A').length,
      other: rows.filter((r) => r.tier === 'B').length,
      working: rows.filter((r) => jobState(r) === 'working').length,
      shipped: rows.filter((r) => jobState(r) === 'shipped').length,
      journal: rows.filter((r) => journal.get(r.id)?.entries).length,
      indexRequested: rows.filter((r) => journal.get(r.id)?.indexRequestedAt).length,
    };
  }, [rows, journal, jobState]);

  const selected = useMemo(() => rows.find((r) => r.id === selectedId) ?? null, [rows, selectedId]);

  if (expandedSection !== 'pages') return null;

  if (selected) {
    return (
      <div ref={sectionRef} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)', minWidth: 0 }}>
        <div>
          <Button variant="ghost" size="sm" icon="chevron-left" onClick={() => setSelectedId(null)}>
            All pages
          </Button>
        </div>
        <PageDetailView
          key={selected.id}
          client={client}
          page={selected}
          path={pathOf(selected.url, mainHost)}
          offers={offers}
          onPageChange={(saved) => setRows((prev) => prev.map((r) => (r.id === saved.id ? saved : r)))}
        />
      </div>
    );
  }

  const header = (label: string, field: SortField, title?: string) => <SortHeader label={label} field={field} sort={sort} onSort={setSort} title={title} />;

  // A person's call beats the scan's: flipping the tier marks it manual, and the scan leaves it alone after that.
  const flipTier = async (row: PageRow) => {
    const tier = row.tier === 'A' ? 'B' : 'A';
    const { data, error: flipError } = await supabase
      .from('client_pages')
      .update({ tier, tier_source: 'manual', tier_reason: `${tier}: set by hand` })
      .eq('id', row.id)
      .select('*');
    if (flipError || !data?.length) {
      toast.error('Couldn’t change the tier', flipError?.message ?? 'Nothing was saved.');
      return;
    }
    setRows((prev) => prev.map((r) => (r.id === row.id ? data[0] : r)));
  };

  const columns: TableColumn<PageRow>[] = [
    {
      key: 'tier',
      label: header('Tier', 'tier', 'A = money page (a lander for something they sell), B = everything else. Click a tier to change it.'),
      width: '5%',
      render: (_v, row) =>
        row.tier ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              void flipTier(row);
            }}
            title={`${row.tier_reason ?? ''}${row.tier_source === 'manual' ? '' : '\nClick to switch to ' + (row.tier === 'A' ? 'B' : 'A')}`}
            style={{
              all: 'unset',
              cursor: 'pointer',
              fontSize: 'var(--text-xs)',
              fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'],
              color: row.tier === 'A' ? 'var(--accent-text)' : 'var(--text-faint)',
              textDecoration: row.tier_source === 'manual' ? 'underline dotted' : 'none',
            }}
          >
            {row.tier}
          </button>
        ) : (
          <span style={faint}>—</span>
        ),
    },
    {
      key: 'url',
      label: header('Page', 'url'),
      width: '23%',
      render: (_v, row) => (
        <span style={oneLine} title={row.title ? `${row.title}\n${row.url}` : row.url}>
          <span style={{ fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'] }}>{pathOf(row.url, mainHost)}</span>
          {row.title ? <span style={{ ...faint, fontSize: 'var(--text-xs)' }}> · {row.title}</span> : null}
        </span>
      ),
    },
    { key: 'gsc_clicks', label: header('Clicks', 'gsc_clicks'), width: '8%', align: 'right', mono: true, render: (_v, row) => <Moved now={row.gsc_clicks} before={row.gsc_clicks_prev} /> },
    { key: 'gsc_impressions', label: header('Impr', 'gsc_impressions', 'Impressions'), width: '10%', align: 'right', mono: true, render: (_v, row) => <Moved now={row.gsc_impressions} before={row.gsc_impressions_prev} /> },
    { key: 'gsc_ctr', label: header('CTR', 'gsc_ctr'), width: '6%', align: 'right', mono: true, render: (v) => (v == null ? <span style={faint}>—</span> : fmtPct(v as number)) },
    { key: 'gsc_position', label: header('Pos', 'gsc_position', 'Average position'), width: '8%', align: 'right', mono: true, render: (_v, row) => <Moved now={row.gsc_position} before={row.gsc_position_prev} lowerIsBetter digits={1} /> },
    { key: 'gsc_queries', label: header('Queries', 'gsc_queries', 'Queries the page showed for'), width: '8%', align: 'right', mono: true, render: (_v, row) => <Moved now={row.gsc_queries} before={row.gsc_queries_prev} /> },
    {
      key: 'journal_last',
      label: 'Last journal',
      width: '11%',
      render: (_v, row) => {
        const last = journal.get(row.id)?.last;
        if (!last) return <span style={faint}>—</span>;
        const label = JOURNAL_SHORT[last.kind] ?? last.kind;
        return (
          <span style={{ ...oneLine, fontSize: 'var(--text-xs)' }} title={`${label}, ${new Date(last.at).toLocaleString()}`}>
            <span className="t-num" style={faint}>{fmtShortDate(last.at)}</span> {label}
          </span>
        );
      },
    },
    {
      key: 'index_coverage',
      label: header('Index', 'index_coverage', 'Google’s index status for the page, from Search Console’s URL Inspection. Checked each sync.'),
      width: '9%',
      render: (_v, row) => {
        if (!row.index_coverage) return <span style={faint}>—</span>;
        const [label, color] = INDEX_SHORT[row.index_coverage] ?? [row.index_coverage, 'var(--text-muted)'];
        const crawled = row.index_last_crawl ? `last crawled ${fmtShortDate(row.index_last_crawl)}` : 'never crawled';
        const checked = row.index_checked_at ? `, checked ${fmtWhen(row.index_checked_at)}` : '';
        return (
          <span style={{ ...oneLine, fontSize: 'var(--text-xs)', color }} title={`${row.index_coverage}\n${crawled}${checked}`}>
            {label}
          </span>
        );
      },
    },
    { key: 'inlinks', label: header('Links in', 'inlinks', 'Internal links pointing at the page'), width: '7%', align: 'right', mono: true, render: (v) => (v == null ? <span style={faint}>—</span> : fmtInt(v as number)) },
    {
      key: 'in_crawl',
      label: 'Flags',
      width: '9%',
      render: (_v, row) => {
        const flags = pageFlags(row);
        return flags.length ? (
          <span style={{ ...oneLine, fontSize: 'var(--text-xs)', color: 'var(--status-warn)' }} title={flags.join(' · ')}>{flags.join(' · ')}</span>
        ) : (
          <span style={faint}>—</span>
        );
      },
    },
  ];

  const synced = rows[0]?.synced_at ?? null;
  // A sync of a project with no GSC leaves every gsc_* column null.
  const gscRow = rows.find((r) => r.gsc_date_from != null);
  const hasGsc = gscRow != null;
  const count = filtered.length === rows.length ? `${fmtInt(rows.length)} pages` : `${fmtInt(filtered.length)} of ${fmtInt(rows.length)} pages`;
  const summary = (label: string, value: React.ReactNode) => (
    <span style={{ whiteSpace: 'nowrap' }}>
      <span style={faint}>{label} </span>
      <span style={{ color: 'var(--text-body)' }}>{value}</span>
    </span>
  );

  return (
    <div ref={sectionRef} style={{ minWidth: 0 }}>
      <Card
        eyebrow="Pages"
        title={<span style={{ whiteSpace: 'nowrap' }}>{rows.length ? count : isLoading ? 'Loading...' : 'No pages yet'}</span>}
        actions={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {projectId !== undefined ? (
              <AhrefsProjectPicker
                clientId={client.id}
                clientName={client.name}
                projectId={projectId}
                onChange={(next) => {
                  setProjectId(next);
                  setSyncError(null);
                }}
                size="sm"
                style={{ width: '200px' }}
              />
            ) : null}
            {rows.length ? (
              <>
                <Switch checked={!hideB} onChange={(on) => toggleHideB(!on)} label={<span style={{ fontSize: 'var(--text-xs)', whiteSpace: 'nowrap' }}>B pages</span>} />
                {/* Input's own wrapper is 100% wide, which takes a whole line of this wrapping row. */}
                <div style={{ width: '200px' }}>
                  <Input icon="search" fieldSize="sm" placeholder="Search pages..." value={search} onChange={(e) => setSearch(e.target.value)} />
                </div>
                <Dropdown
                  size="sm"
                  value={show}
                  onChange={(e) => setShow(e.target.value)}
                  options={[
                    { value: 'all', label: 'All pages' },
                    { value: 'money', label: 'Money pages (A)' },
                    { value: 'other', label: 'Other pages (B)' },
                    ...(offers.length ? [{ value: 'no-offer', label: 'Money pages, no offer' }] : []),
                    ...offers.map((o) => ({ value: `offer:${o.id}`, label: `Offer: ${o.name}` })),
                    { value: 'clicks', label: 'Getting clicks' },
                    { value: 'unclicked', label: 'Seen, never clicked' },
                    { value: 'unseen', label: 'Live, never seen' },
                    { value: 'flagged', label: 'Flagged' },
                    { value: 'over', label: 'Over-optimized' },
                    { value: 'under', label: 'Under-optimized' },
                    { value: 'templated', label: 'Templated' },
                    { value: 'few-links', label: `Few links in (under ${LINK_FLOOR})` },
                    { value: 'journal', label: 'Has journal entries' },
                    { value: 'unseen-unrequested', label: 'Never seen, not requested' },
                    { value: 'index-requested', label: 'Indexing requested' },
                    { value: 'working', label: 'Agent working' },
                    { value: 'shipped', label: 'Shipped since sync' },
                  ]}
                  style={{ width: '170px' }}
                />
                <Button variant="ghost" size="sm" icon="refresh-cw" onClick={fetchRows} disabled={isLoading}>
                  Refresh
                </Button>
              </>
            ) : null}
          </div>
        }
      >
        {error ? <div style={{ color: 'var(--danger-400)', fontSize: 'var(--text-sm)', marginBottom: 'var(--space-4)' }}>{error}</div> : null}
        {syncError ? (
          <div style={{ color: 'var(--status-warn)', fontSize: 'var(--text-sm)', marginBottom: 'var(--space-4)' }}>
            The last sync ({fmtWhen(syncError.at)}) didn’t complete: {syncError.message}
          </div>
        ) : null}
        {rows.length === 0 ? (
          isLoading ? null : (
            <div style={{ padding: '28px 0', textAlign: 'center', color: 'var(--text-muted)', fontSize: 'var(--text-sm)', lineHeight: 1.6 }}>
              {projectId === null
                ? `Pick ${client.name}’s Ahrefs project above. The nightly sync then pulls every live URL from its site crawl, with its Search Console performance.`
                : `The nightly sync fills this from ${client.name}’s Ahrefs project: every live URL from the site crawl, with its Search Console performance.`}
            </div>
          )
        ) : (
          <>
            <div className="t-num" style={{ display: 'flex', gap: '6px var(--space-5)', flexWrap: 'wrap', fontSize: 'var(--text-xs)', marginBottom: 'var(--space-3)' }}>
              {hasGsc ? summary('Clicks', <Moved now={totals.clicks} before={totals.clicksPrev} />) : null}
              {hasGsc ? summary('Impressions', <Moved now={totals.impressions} before={totals.impressionsPrev} />) : null}
              {summary(syncLimit ? `Top ${fmtInt(syncLimit)} pages by traffic` : 'Live pages', fmtInt(totals.live))}
              {hasGsc ? summary('Never seen in search', `${fmtInt(totals.unseen)}${totals.live ? ` (${Math.round((100 * totals.unseen) / totals.live)}%)` : ''}`) : null}
              {(
                [
                  ['money', 'Money pages', totals.money, 'Tier A: landers for something they sell. Show them'],
                  ['other', 'Other', totals.other, 'Tier B: articles, case studies, hubs. Show them'],
                  ['over', 'Over-optimized', totals.over, 'A search phrase above 1% of the words. Show them'],
                  ['under', 'Under-optimized', totals.under, 'The page’s target below 0.5% of the words. Show them'],
                  ['templated', 'Templated', totals.templated, 'Half or more of the page is also on another page of the site. Show them'],
                  ['few-links', 'Few links in', totals.fewLinks, `Fewer than ${LINK_FLOOR} internal links point at the page, so Google crawls it rarely and reads it as unimportant. Show them`],
                  ['working', 'Agent working', totals.working, 'An agent job is open on the page (amber left bar). Show them'],
                  ['shipped', 'Shipped since sync', totals.shipped, 'An agent job shipped after the last sync (green left bar); the next sync re-measures them. Show them'],
                  ['journal', 'With journal entries', totals.journal, 'Pages with a Retroactive Optimization, Keyword Density Fix, Templated Content Fix, Internal Links Fix or indexing request on record. Show them'],
                  ['index-requested', 'Indexing requested', totals.indexRequested, 'Pages someone asked Google to recrawl in Search Console (logged from the page journal). Show them'],
                ] as const
              ).map(([value, label, n, title]) =>
                n ? (
                  <button key={value} type="button" onClick={() => setShow(value)} title={title} style={{ all: 'unset', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    <span style={faint}>{label} </span>
                    <span
                      style={{
                        color:
                          value === 'money'
                            ? 'var(--accent-text)'
                            : value === 'other' || value === 'journal' || value === 'index-requested'
                              ? 'var(--text-body)'
                              : value === 'under' || value === 'working'
                                ? 'var(--status-warn)'
                                : value === 'shipped'
                                  ? 'var(--status-up)'
                                  : 'var(--status-down)',
                      }}
                    >
                      {fmtInt(n)}
                    </span>
                  </button>
                ) : null,
              )}
              <span style={{ flex: 1 }} />
              {hasGsc ? (
                <span style={faint}>
                  Search Console {fmtDay(gscRow?.gsc_date_from ?? null)} – {fmtDay(gscRow?.gsc_date_to ?? null)} · synced {fmtWhen(synced)}
                </span>
              ) : (
                <span style={{ color: 'var(--status-warn)' }}>
                  Search Console isn’t connected to this site’s Ahrefs project — crawl facts only · synced {fmtWhen(synced)}
                </span>
              )}
            </div>
            {filtered.length === 0 ? (
              <div style={{ padding: '28px 0', textAlign: 'center', color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>Nothing matches those filters.</div>
            ) : (
              <div style={{ overflowX: 'auto', margin: '0 calc(-1 * var(--space-2))' }}>
                <Table
                  columns={columns}
                  rows={filtered}
                  dense
                  style={flatTable}
                  rowStyle={(row) => {
                    // Background: Google's verdict. Red = not indexed; yellow = not indexed but indexing was requested
                    // in the last 30 days, so it's waiting on Google. Left bar: an agent job working (amber) or shipped
                    // since the last sync (green); on an indexed page the job colour fills the row as before.
                    const st = jobState(row);
                    const bar = st === 'working' ? 'inset 3px 0 0 var(--status-warn)' : st === 'shipped' ? 'inset 3px 0 0 var(--status-up)' : undefined;
                    if (row.index_coverage && row.index_verdict !== 'PASS') {
                      const requested = journal.get(row.id)?.indexRequestedAt;
                      const recent = requested != null && Date.now() - new Date(requested).getTime() < INDEX_REQUEST_FRESH_MS;
                      return { background: recent ? 'var(--status-warn-soft)' : 'var(--status-down-soft)', boxShadow: bar };
                    }
                    if (st === 'working') return { background: 'var(--status-warn-soft)', boxShadow: bar };
                    if (st === 'shipped') return { background: 'var(--status-up-soft)', boxShadow: bar };
                    return undefined;
                  }}
                  onRowClick={(row) => {
                    listScroll.current = window.scrollY;
                    setSelectedId(row.id);
                  }}
                />
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
